<template>
  <div id="lot-info-card-main" class="lot-info-card box-shadow">
    <magnifier
      swiper-main-class=".main-lot-image .swiper-wrapper"
      zoom-elements-class=".magnifier-zoom-image .magnifier-zone"
      type="gallery"
      :files-prop="popupLotFiles"></magnifier>

    <h1 class="item-title">
      <a :href="popupLot.href">
        {{ trans("live.catalog.lot_card.lot_number", { number: popupLot.lot_number }) }}: {{ popupLot.title }}
      </a>
    </h1>

    <div :class="['item-categories', { 'no-margin': !popupLot.description || popupLot.description?.length === 0 }]">
      {{ popupLot.parent_category }} / {{ popupLot.category }}
    </div>
    <div class="bid-confirm-wrapper" :class="{ 'is-confirm-step': state.isConfirmationStep }">
      <div class="bid-confirm-place">
        <custom-scrollbar class="custom-scrollbar lot-details-popup-description-scroller">
          <div class="description" v-html="popupLot.description"></div>

          <div class="metas">
            <template v-if="popupLot.catalogs && popupLot.catalogs.length">
              <div class="metas__row catalog">
                {{ trans("live.catalog.catalog") }}
                <template v-for="(catalog, index) in popupLot.catalogs" :key="index">
                  <span class="value">
                    {{ catalog.name }} {{ catalog.number }}<span v-if="index !== popupLot.catalogs.length - 1" class="value">, </span>
                  </span>
                </template>
              </div>

              <div class="metas__row catalog-value" v-if="catalogsWithValue?.length">
                {{ trans("live.catalog.catalog_value") }}
                <span class="value">{{ formatCatalogValueRange(popupLot) }}</span>
              </div>
            </template>

            <div
              v-if="(popupLot.conditions && popupLot.conditions.length) || popupLot.condition_notes"
              class="metas__row condition">
              {{ trans("live.catalog.condition") }}
              <span
                v-if="popupLot.conditions.length"
                class="value"
                v-html="popupLot.conditions.map((condition) => condition.name).join(', ') + '. '"></span>
              {{ popupLot.condition_notes }}
            </div>
          </div>
        </custom-scrollbar>

        <bid-status
          v-if="
          [LotStatuses.LIVE, LotStatuses.ENDED].includes(popupLot.status) &&
          [AuctionStatuses.LIVE, AuctionStatuses.ENDED].includes(auctionStatus)
        "
          class="top"
          :lot-data="popupLot"
          :user-bid-prop="state.userBid"
          :hidden-pending-prop="true"
          @update:isPending="updateIsPending"
        ></bid-status>

        <div class="lot-info-bid-fixed-block-wrapper" ref="fixedInfoBidWrapper">
          <div class="lot-info-bid-fixed-block" ref="fixedInfoBidBlock">
            <div class="current-bid-block">
              <div :class="['current-bid', soldStatus?.toLowerCase()]">
                <template v-if="soldStatus === LotSoldStatuses.UNSOLD">
                  <div class="bid-status">
                    <div class="label">{{ trans("lot.card_info_block.bid_labels.unsold") }}</div>
                  </div>
                </template>

                <template v-else-if="soldStatus === LotSoldStatuses.SOLD">
                  <div class="bid-status">
                    <div class="label">{{ trans("lot.card_info_block.bid_labels.lot_sold") }}</div>
                    <div class="value">{{ format_currency(popupLot.sale_price) }}</div>
                  </div>
                </template>

                <template v-else>
                  <current-bid class="lot-card__current-bid" :current-bid-prop="popupCurrentBid"></current-bid>

                  <div v-if="!isCurrencyBase" class="lot-card__converted-bid">
                    {{ trans("lot.card_info_block.bid_labels.converted") }}
                    <converted-amount :amount-prop="popupLot.current_bid"></converted-amount>
                  </div>
                </template>
              </div>

              <bids-count />
            </div>

            <template v-if="[AuctionStatuses.ACTIVE, AuctionStatuses.LIVE].includes(auctionStatus)">
              <div
                class="place-bid-block"
                :class="{ 'with-status-bid': state.isPending || state.userBid }"
                v-if="popupLot.status == LotStatuses.PREPARED"
              >
                <bid-status
                  :lot-data="popupLot"
                  :user-bid-prop="state.userBid"
                  @update:isPending="updateIsPending"
                ></bid-status>

                <div class="lot-card__fast-bid big">
                  <select-bid-dropdown
                    direction-vertical-prop="bottom"
                    :lot-data="popupLot"
                    :user-bid-prop="state.userBid"
                    :current-bid-prop="popupCurrentBid"
                    :bids-count-prop="bidsCount"
                    @update:selectedBid="updateSelectedBid"
                    ref="selectBidDropdownFirst"></select-bid-dropdown>

                  <select-bid-btn
                    :user-bid-prop="state.userBid"
                    place-bid-prop="lot-page"
                    @select-bid="selectBid"></select-bid-btn>
                </div>
              </div>

              <div
                class="reopen-block"
                v-else-if="
                auctionStore.user?.is_admin &&
                popupLot.status === LotStatuses.ENDED
              "
              >
                <a href="#" class="btn btn-blue w-100" @click.prevent="reopenLot">
                  {{
                    reopenLotState === 'reopen'
                      ? trans('live.action_buttons.reopen_lot_label')
                      : trans('live.action_buttons.confirm_reopen_lot_label')
                  }}
                </a>
              </div>
            </template>
          </div>
        </div>

        <div class="actions">
          <favorite-button
            :lot-id-prop="popupLot.id"
            :subscribers-count-prop="popupLot.subscribers_count"
            show-count
          ></favorite-button>
          <lot-currency-btn v-if="auctionStatus !== AuctionStatuses.ENDED && soldStatus !== LotSoldStatuses.UNSOLD">
            <div class="currency-btn">
              <svg class="icon-coin">
                <use href="/sprite.svg#coin"></use>
              </svg>
              {{ auctionStore.currency }}
            </div>
          </lot-currency-btn>
        </div>
      </div>
      <div class="bid-confirm-block">
        <div class="inner">
          <current-bid class="row-bid-info" :current-bid-prop="popupCurrentBid"></current-bid>

          <div class="row-bid-info with-place-bid">
            <div class="label">{{ trans("lot.confirmation.maximum_bid") }}</div>
            <select-bid-dropdown
              dropdown-custom-class="invert"
              :lot-data="popupLot"
              :user-bid-prop="state.userBid"
              :current-bid-prop="popupCurrentBid"
              :bids-count-prop="bidsCount"
              @update:selectedBid="updateSelectedBid"
              ref="selectBidDropdownSecond"></select-bid-dropdown>
          </div>

          <div class="row-bid-info">
            <div class="label">{{ trans("lot.confirmation.buyers_premium") }}</div>
            <div class="value">{{ format_currency(state.buyersPremium) }}</div>
          </div>

          <div class="row-bid-info" :class="{ last: isCurrencyBase }">
            <div class="label">{{ trans("lot.confirmation.total_amount") }}</div>
            <div class="value">{{ format_currency(state.totalAmount) }}</div>
          </div>

          <div v-if="!isCurrencyBase" class="row-bid-info last converted-bid">
            <div class="label">{{ trans("lot.confirmation.converted_total") }}</div>
            <converted-amount :amount-prop="state.totalAmount"></converted-amount>
          </div>

          <div
            class="description-bid"
            v-html="trans('lot.confirmation.description').replace(':link', '/conditions-of-sale')"></div>
        </div>

        <div class="bid-btns">
          <button
            class="btn btn-blue-outline"
            @click="cancelBid"
            v-html="trans('lot.confirmation.buttons.cancel')"></button>

          <place-bid-btn @place-bid="placeBid"></place-bid-btn>
        </div>
      </div>
    </div>
  </div>

  <auth-popup :message="trans('lot.card_info_block.place_bid_btn.notify_for_login')" ref="authPopupRef" />
  <error-popup-wrapper ref="errorPopupRef"></error-popup-wrapper>
</template>

<script lang="ts" setup>
  import {computed, markRaw, onMounted, onUnmounted, reactive, ref, watch} from "vue";
  import {useAuctionStore} from "@/stores/AuctionStore";
  import {format_currency} from "@/utils/currencies";
  import {trans} from "@/utils/trans";
  import {formatCatalogValueRange} from "@/utils/lot";
  import {initPopupBidsCountListener, initPopupCurrentBidListener, releaseSharedListener} from "@/utils/websockets";
  import FavoriteButton from "@/components/_partials/FavoriteButton.vue";
  import Magnifier from "@/components/_partials/MagnifierOuter.vue";
  import {
    AuthPopup,
    BidsCount,
    BidStatus,
    ConvertedAmount,
    CurrentBid,
    ErrorPopupWrapper,
    LotBidsList,
    LotCurrencyBtn,
    PlaceBidBtn,
    SelectBidBtn,
    SelectBidDropdown
  } from "@/components/_partials/lot-info-popup";

  import {ILotPopupData} from "@/models";
  import {isScreenT} from "@/utils/media-size";
  import {AuctionStatuses, LotSoldStatuses, LotStatuses} from "@/enum";
  import apiClient from "@/services/api";
  import CustomScrollbar from "@/components/_partials/CustomScrollbar.vue";

  const emit = defineEmits(["closeForm"]);

  const auctionStore = useAuctionStore();
  const selectBidDropdownFirst = ref<HTMLDivElement>(null);
  const selectBidDropdownSecond = ref<HTMLDivElement>(null);
  const authPopupRef = ref<HTMLDivElement>(null);
  const errorPopupRef = ref<HTMLDivElement>(null);

  const popupLot = computed(() => auctionStore.popupLot as ILotPopupData);
  const auctionStatus = computed(() => auctionStore.auction.status);
  const popupLotFiles = computed(() => popupLot.value.values?.files);
  const soldStatus = computed(() => popupLot.value?.sold_status);
  const catalogsWithValue = computed(() => popupLot.value?.catalogs?.filter(c => c.value !== null));

  const popupCurrentBid = computed(() => popupLot.value.current_bid);
  const bidsCount = computed(() => popupLot.value.bids_count);
  const isCurrencyBase = computed(() => auctionStore.isCurrencyBase);

  const reopenLotState = ref<'reopen' | 'confirm'>('reopen');

  const state = reactive({
    lotId: popupLot.value.id,
    buyersPremium: 0,
    totalAmount: 0,

    selectBidBtnLabel: "",
    selectedBid: 0,
    userBid: auctionStore.user?.bids[popupLot.value.id] ?? 0,

    isConfirmationStep: false,
    isPending: false,

    bidsListComponent: markRaw(LotBidsList),
    bidsList: []
  });

  watch(
    () => popupLot.value.status,
    (newValue) => {
      if (newValue === LotStatuses.LIVE) {
        state.isConfirmationStep = false;
      }
    }
  );

  watch(
    () => state.selectedBid,
    (newValue) => {
      if (selectBidDropdownFirst.value) {
        selectBidDropdownFirst.value.selectItem(newValue);
      }
      if (selectBidDropdownSecond.value) {
        selectBidDropdownSecond.value.selectItem(newValue);
      }

      state.buyersPremium = calcBuyersPremium(newValue);

      state.totalAmount = state.selectedBid + state.buyersPremium;
    }
  );

  onMounted(() => {
    initPopupCurrentBidListener(popupLot.value);
    initPopupBidsCountListener(popupLot.value);
  });

  onUnmounted(() => {
    releaseSharedListener("popupCurrentBidListener", "currentBidListener");
    releaseSharedListener("popupBidsCountListener", "bidsCountListener");
  });

  const updateSelectedBid = (value) => {
    state.selectedBid = value;
  };
  const selectBid = () => {
    if (!auctionStore.user?.id) {
      authPopupRef.value?.open();
      return;
    }
    changeConfirmationStep(true);
  };
  const placeBid = () => {
    if (auctionStore.user.id) {
      apiClient
        .post("/api/lots/" + state.lotId + "/place-bid", {
          amount: state.selectedBid
        })
        .then((response) => {
          if (response.data.success) {
            changeConfirmationStep(false);
            state.userBid = response.data.amount;
          }
        })
        .catch((error) => {
          console.log(error);

          const errors = error.response.data.errors;
          if (Object.keys(errors).length) {
            errorPopupRef.value?.openPopup(errors);
            state.isConfirmationStep = false;
          }
        });
    }
  };

  const cancelBid = () => {
    changeConfirmationStep(false);
  };

  const updateIsPending = (value) => {
    state.isPending = value;
  };

  const calcBuyersPremium = (price) => {
    if (popupLot.value.buyers_premium.type === "Percent") {
      let percent = popupLot.value.buyers_premium.value;
      return state.selectedBid * (+percent / 100);
    } else if (popupLot.value.buyers_premium.type === "Range") {
      let rules = popupLot.value.buyers_premium.value;
      for (let i = rules.length - 1; i >= 0; i--) {
        if (price >= rules[i].min_sale_price) {
          return state.selectedBid * (+rules[i].fee_percent / 100);
        }
      }
    }

    return 0;
  };

  const makeProxyBid = () => {
    if (!auctionStore.user?.id) {
      authPopupRef.value?.open();
      return;
    }
    changeConfirmationStep(true);
  };

  const reopenLot = async () => {
    if (reopenLotState.value === 'reopen') {
      reopenLotState.value = 'confirm';
    } else {
      await auctionStore
        .reopenLot(popupLot.value)
        .then((response) => {
          emit("closeForm");
        })
        .catch((error) => {
          console.log(error);
        });

      reopenLotState.value = 'reopen';
    }
  };

  const changeConfirmationStep = (value) => {
    state.isConfirmationStep = value;

    if (isScreenT.value) {
      if (value) {
        document.body.classList.add("overflow-hidden");
      } else {
        document.body.classList.remove("overflow-hidden");
      }
    }
  };

  const close = () => (auctionStore.popupLot = null);
</script>
